// S9 · Terminology settings copy (Module 02 §4 S9, §2.5, D-032/D-035).
//
// Spliced into the `onboarding` namespace as `onboarding.terminology.*` by
// ../../onboarding.i18n.ts, which carries it into the four-locale CI parity gate
// (D-014). ru is TRANSLATED (terminology.ru.ts); zh/fr still render English —
// declared in src/i18n-completeness.ts.
//
// The 61 canonical TERM names themselves are NOT repeated here — they live once,
// as `onboarding.terms.*` (the base labels of §2.5), and this screen renders
// them from there. Duplicating them would create a second source of truth for
// the very thing this screen edits.

export const terminologySettingsEn = {
  title: "Terminology",
  subtitle: "What your factory calls things. A rename changes it everywhere.",
  loading: "Loading your terminology…",
  errorTitle: "Could not load your terminology",
  errorDetail: "The term list could not be read. Try again.",
  emptyTitle: "No terms to show",
  emptyBody: "Nothing matches your search. Clear the filters to see every term.",
  emptyCta: "Clear filters",
  noTermsTitle: "No terminology is configured",
  noTermsBody: "No terms are set up for your factory yet. Try again, or ask your Assessly contact.",
  readOnlyNote: "Only a group admin can rename terms. You can read every value here.",

  // §2.5 / D-035 — the one nuance a user must not be surprised by.
  layerNote:
    "Each value is whichever layer wins: your own wording, then your industry pack's, then the standard label.",
  resetNote:
    "Reset removes your wording and goes back to the standard label. Your pack's wording returns on its next install.",

  // ---- toolbar -------------------------------------------------------------
  searchLabel: "Search terms",
  searchPlaceholder: "Search a term or a word you use…",
  sourceLabel: "Show",
  source: {
    all: "Everything",
    overridden: "Only what I renamed",
    tenant: "Renamed by us",
    pack: "From our industry pack",
    base: "Standard label",
  },
  stats: "{{terms}} terms · {{overridden}} renamed by you",
  // One page of 20 terms at a time (design-system §3.17 rev-7); the range
  // counts the rows the search and the source filter leave.
  pageRange: "Terms {{from}}–{{to}} of {{total}}",
  prevPage: "Previous page",
  nextPage: "Next page",
  clear: "Clear filters",

  // ---- grid ----------------------------------------------------------------
  colTerm: "Term",
  colLocale: {
    en: "English",
    ru: "Russian",
    zh: "Chinese",
    fr: "French",
  },
  sourceBadge: {
    tenant: "Ours",
    pack: "Pack",
    base: "Standard",
  },
  sourceTitle: {
    tenant: "Your own wording for this term.",
    pack: "Supplied by your industry pack.",
    base: "The standard Assessly label.",
  },
  cellLoading: "Loading…",
  cellMissing: "Not loaded",

  // ---- inline edit ---------------------------------------------------------
  edit: "Rename",
  editLabel: "{{term}} in {{locale}}",
  save: "Save",
  saving: "Saving…",
  cancel: "Cancel",
  reset: "Reset",
  resetting: "Resetting…",
  charCount: "{{used}} of {{max}} characters",
  errorEmpty: "Enter a word — a term cannot be blank.",
  errorTooLong: "Keep it to {{max}} characters or fewer.",
  saveFailed: "Could not save that rename. Try again.",
  resetFailed: "Could not reset that term. Try again.",
  savedTenant: "Saved — this is your wording now.",
  resetToBase: "Reset — this term shows the standard label again.",
  resetToPack: "Reset — this term shows your industry pack's wording again.",

  overwritePackCaution:
    "This wording came from your industry pack. Saving replaces it, and Reset then returns the standard wording — not the pack's.",
  resetConfirmTitle: "Reset this wording?",
  resetConfirmBody:
    "\u201c{{label}}\u201d ({{term}}, {{locale}}) goes back to the wording underneath. This cannot be undone from here.",
} as const;
